// Support Framework §8 / §10 — "why does this date exist?" copy for the
// Manager (and HR) touch-point timeline.
//
// docs/13 §8 specifies an EXPANDED touch point card carrying "Purpose — why
// this touch point exists" alongside the manager lens, and §10 specifies a
// "Context — short explanation of why this matters" on the manager card. The
// timeline shipped with the collapsed half only (name · date · status), so a
// manager saw a schedule with no reasoning behind it — while the EMPLOYEE
// portal has carried its own softened purpose copy since day one
// (`SUPPORT_MOMENTS` in `apps/web/src/components/SupportPathwayView.tsx`).
// This module is the manager/HR-facing other half of that same column.
//
// DELIBERATELY STATIC, NOT MAEVE-GENERATED. The PO's framing was "click it and
// Maeve will tell them why that date is important", but ten fixed facts about
// a fixed framework are not an inference problem: static copy costs no tokens
// or latency on a control people click idly, writes no `maeve_interactions`
// audit row per click, and cannot drift from the framework it describes.
// Nothing here is attributed to Maeve — presenting authored product copy as
// Maeve speech would breach the §1.4 #8 anti-anthropomorphism rule as surely
// as a first-person greeting does. The genuinely generative half of the job —
// "and what do I say?" — already exists one panel below as the Sub-mode C
// briefing (`ManagerMaeveTouchpointPrep`), which each expanded row links to.
//
// NOT YET CGC-SIGNED-OFF. This is clinical framing shown to a manager, so it
// carries the same gate as the rest of the framework's content. `timing` is
// mechanical (it restates `SUPPORT_FRAMEWORK_STAGES`' anchor + offset and is
// not clinical); `why` and `focus` are drafted from DESIGN §6.3's Purpose
// column, docs/13's stage table and §16's "not a performance conversation or
// a welfare assessment" constraint, and the already-shipped employee-facing
// purposes. They need CGC review before production — see PROJECT.md's
// "Remaining work — non-engineering".
//
// `focus` is scoped to what the touch point is FOR, never to what to say. The
// framework is explicit that managers must not be handed a script (§10.1.2 #3),
// and `ManagerMaeveTouchpointPrep` owns the conversation-pointer job.

import type { TouchpointType } from './cases.ts';

export interface TouchpointRationale {
  /**
   * When it falls, in words. Names the anchor ONLY for the touch points whose
   * anchor is unambiguous — `Day_3`/`Week_1`/`Month_12`/`Anniversary` are always
   * loss-anchored and `Pre_Return`/`Week_1_Back` are always RTW-anchored, but
   * `Month_1/3/6/9` are hybrid: RTW-anchored since the §17 re-anchoring, still
   * loss-anchored on any case created before it (see `RTW_TOUCHPOINT_TYPES` in
   * `./cases.ts`). A materialised row carries no marker of which it was, so those
   * four say "N months in" and leave the anchor to the row's own date and its
   * stage header rather than asserting something that is wrong for older cases.
   */
  timing: string;
  /** Why the framework puts a touch point here at all. */
  why: string;
  /** What this touch point is for. NOT what to say — see the header note. */
  focus: string;
}

export const TOUCHPOINT_RATIONALE: Record<TouchpointType, TouchpointRationale> = {
  Day_3: {
    timing: 'Three days after the date of loss.',
    why: 'The first days are practical and disorienting. An early, human message tells someone their workplace already knows, so they are not left deciding when to make the first move themselves.',
    focus:
      'A brief personal message. No work topics, and no questions about when they might return.',
  },
  Week_1: {
    timing: 'One week after the date of loss.',
    why: 'By the end of the first week, leave, pay and who is covering the work all start to matter — and are exactly the things a bereaved employee tends not to ask about.',
    focus:
      'A wellbeing check-in that also makes leave and entitlements clear without them having to chase.',
  },
  Pre_Return: {
    timing: 'Shortly before the confirmed return-to-work date.',
    why: 'Returning is one of the hardest points in the pathway. Knowing in advance what the first days will actually look like removes a layer of dread from it.',
    focus:
      'A practical conversation about hours, workload, and what happens if they need to step away.',
  },
  Week_1_Back: {
    timing: 'One week after the return-to-work date.',
    why: 'The first week back is where the gap is widest between how someone appears at work and how they are.',
    focus: 'How the return is actually going, and whether anything agreed needs adjusting.',
  },
  Month_1: {
    timing: 'One month in.',
    why: 'The support from colleagues, friends and family usually thins out around now, while the demands of work resume in full.',
    focus:
      'Ongoing needs — workload, flexibility, and anything agreed earlier that is not working.',
  },
  Month_3: {
    timing: 'Three months in.',
    why: 'Grief commonly resurfaces once the immediate crisis has passed — which is also the point at which organisations tend to assume the matter is closed.',
    focus:
      'A longer-term check-in. It is not a performance conversation and not a welfare assessment.',
  },
  Month_6: {
    timing: 'Six months in.',
    why: 'Support quietly disappearing is itself part of the problem this framework exists to fix.',
    focus: 'Light-touch contact confirming that support is still there if it is wanted.',
  },
  Month_9: {
    timing: 'Nine months in.',
    why: 'The approach to the first anniversary often starts to weigh on someone well before the date itself.',
    focus:
      'Light-touch contact, and room to plan ahead for the anniversary if they would find that helpful.',
  },
  Month_12: {
    timing: 'Around twelve months after the date of loss.',
    why: 'The first anniversary is frequently harder than the months either side of it, and is the point employers most often miss.',
    focus: 'Acknowledge the year, and ask what — if anything — would help around the date.',
  },
  Anniversary: {
    timing: 'On or near the anniversary of the death.',
    why: 'Anniversaries and other significant dates can bring grief back sharply, even for someone who has been coping well.',
    focus:
      'Flexibility around the date usually matters more than a conversation. Offer it rather than waiting to be asked.',
  },
};
